import {LabelToken} from '@github-ui/label-token'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {Link, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {useMemo} from 'react'

import {VALUES} from '../constants/values'
import type {LabelNode} from './issue-viewer-view-query-types'
import labelStyles from './Label.module.css'
import styles from './labels.module.css'

// RELAY-CLEANUP(LabeledEvent): widens nullable fields so the still-Relay parent data is assignable.
// Remove when LabeledEvent and UnlabeledEvent leave Relay.
export type LabelFragment = RelayCompat<Pick<LabelNode, 'color' | 'description' | 'id' | 'name' | 'nameHTML'>>

type LabelProps = {
  label: LabelFragment
  timelineEventBaseUrl: string
}

export function Label({
  label: {nameHTML, name, color, id, description},
  timelineEventBaseUrl,
}: LabelProps): React.ReactElement {
  const url = useMemo(() => {
    return `${timelineEventBaseUrl}?q=${encodeURIComponent(VALUES.labelQuery(name))}`
  }, [timelineEventBaseUrl, name])

  return (
    <Tooltip
      text={description ?? ''}
      className={clsx(labelStyles.labelDescriptionTooltip, !description && labelStyles.hiddenTooltip)}
    >
      <Link href={url} className={styles.labelLink} aria-describedby={`${id}-tooltip`} muted>
        <LabelToken
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to GraphQL fragment LabelData.nameHTML */
          text={<VerifiedHTMLText html={nameHTML as SafeHTMLString} />}
          fillColor={`#${color}`}
          key={id}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '20ch',
            cursor: 'pointer',
          }}
        />
        <span className="sr-only" id={`${id}-tooltip`}>
          {description ?? ''}
        </span>
      </Link>
    </Tooltip>
  )
}
